/**
 * Pagination Arrow with Preview Component
 *
 * Wraps pagination arrow with optional avatar preview.
 * Shows next/previous member's avatar beside the arrow when active.
 * Becomes invisible when disabled.
 * Both arrow and avatar are clickable (triggers pagination).
 *
 * Follows single responsibility: combines avatar preview with arrow navigation.
 */

import { SimpleAvatar } from '@/components/shared/SimpleAvatar';
import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { FamilyMemberResource } from '@/types/family-resource';
import Ionicons from '@expo/vector-icons/Ionicons';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

const AVATAR_SIZE = 16;
const AVATAR_BORDER_WIDTH = 1.5;
const AVATAR_OPACITY = 0.5;
const ICON_SIZE = 20;

type ArrowIconProps = {
  direction: 'left' | 'right';
  disabled: boolean;
};

function ArrowIcon({ direction, disabled }: ArrowIconProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const iconName = direction === 'left' ? 'chevron-back' : 'chevron-forward';
  const iconColor = disabled ? palette.textMuted : palette.text;

  // Render icon directly with marginTop to center at y=21 (21 - 10 = 11)
  return (
    <Ionicons
      name={iconName}
      size={ICON_SIZE}
      color={iconColor}
      style={styles.icon}
    />
  );
}

type PaginationArrowWithPreviewProps = {
  direction: 'left' | 'right';
  onPress: () => void;
  disabled: boolean;
  previewMember: FamilyMemberResource | null;
};

export function PaginationArrowWithPreview({
  direction,
  onPress,
  disabled,
  previewMember,
}: PaginationArrowWithPreviewProps) {
  const showPreview = !disabled && previewMember !== null;
  const isLeft = direction === 'left';

  return (
    <TouchableOpacity
      style={[
        styles.container,
        isLeft ? styles.containerLeft : styles.containerRight,
        disabled && styles.containerDisabled,
      ]}
      onPress={onPress}
      disabled={disabled}
      activeOpacity={0.7}
    >
      {/* Left arrow: arrow icon on left, avatar on right */}
      {isLeft && <ArrowIcon direction="left" disabled={disabled} />}
      {showPreview && previewMember && (
        <View
          style={[
            styles.avatarWrapper,
            { borderColor: previewMember.color, opacity: AVATAR_OPACITY },
          ]}
        >
          <SimpleAvatar
            name={previewMember.name}
            color={previewMember.color}
            size={AVATAR_SIZE}
          />
        </View>
      )}
      {/* Right arrow: avatar on left, arrow icon on right */}
      {!isLeft && <ArrowIcon direction="right" disabled={disabled} />}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    height: 60,
    gap: 0,
  },
  containerLeft: {
    justifyContent: 'flex-start',
  },
  containerRight: {
    justifyContent: 'flex-end',
  },
  containerDisabled: {
    opacity: 0,
  },
  // Position icon center at y=21: icon top at 21-10=11
  icon: {
    marginTop: 11,
  },
  // Position avatar center at y=21: avatar top at 21-10.5≈10
  avatarWrapper: {
    marginTop: 10,
    borderWidth: AVATAR_BORDER_WIDTH,
    borderRadius: (AVATAR_SIZE + AVATAR_BORDER_WIDTH * 2) / 2,
    padding: 1,
  },
});
